import { useEffect, useRef, useState } from "react";
import { FileText, Inbox, Plus, ReceiptText, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";
import { useAuth } from "@/auth";

const emptyLead = { name: "", email: "", phone: "", service_type: "", message: "" };

/** Quick Action (+): apre le creazioni più usate (Preventivo, Lead, Spesa) da qualsiasi
 * pagina, senza dover navigare manualmente al modulo giusto. */
export const QuickActionMenu = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (user.is_demo) return null;
  const canSell = ["admin", "responsabile", "coordinatore_commerciale"].includes(user.role);
  const actions = [
    canSell && { key: "preventivo", label: "Nuovo Preventivo", icon: FileText, onSelect: () => navigate("/preventivi/nuovo") },
    canSell && { key: "lead", label: "Nuovo Lead", icon: Inbox, onSelect: () => setLeadForm({ ...emptyLead }) },
    { key: "spesa", label: "Nuova Spesa", icon: ReceiptText, onSelect: () => navigate("/spese", { state: { quickAction: "spesa" } }) },
  ].filter(Boolean);
  if (!actions.length) return null;

  const submitLead = async (event) => {
    event.preventDefault();
    if (!leadForm.name.trim() || (!leadForm.email.trim() && !leadForm.phone.trim())) {
      toast.error("Indica il nome e almeno email o telefono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/leads", { ...leadForm, email: leadForm.email.trim() || null, phone: leadForm.phone.trim() || null });
      toast.success("Lead creato");
      setLeadForm(null);
      navigate("/lead");
    } catch (error) { toast.error(getErrorMessage(error)); } finally { setBusy(false); }
  };

  return <div className="quick-action-menu" ref={ref} data-testid="quick-action-menu">
    <button type="button" className="quick-action-trigger" data-testid="quick-action-trigger" onClick={() => setOpen((value) => !value)} aria-label="Azione rapida">
      <Plus size={18} />
    </button>
    {open && <div className="quick-action-dropdown" data-testid="quick-action-dropdown">
      {actions.map((action) => <button key={action.key} type="button" className="quick-action-item" data-testid={`quick-action-${action.key}`} onClick={() => { setOpen(false); action.onSelect(); }}>
        <action.icon size={16} />{action.label}
      </button>)}
    </div>}

    {leadForm && <div className="modal-backdrop" data-testid="quick-lead-modal">
      <form className="completion-modal" onSubmit={submitLead}>
        <div className="section-heading"><div><p className="eyebrow">QUICK ACTION</p><h2>Nuovo Lead</h2></div><button type="button" className="icon-button" data-testid="quick-lead-close" onClick={() => setLeadForm(null)}><X size={18} /></button></div>
        <label data-testid="quick-lead-name-label">Nome<input data-testid="quick-lead-name-input" value={leadForm.name} onChange={(event) => setLeadForm((current) => ({ ...current, name: event.target.value }))} required /></label>
        <div className="form-grid" style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label data-testid="quick-lead-email-label">Email<input data-testid="quick-lead-email-input" type="email" value={leadForm.email} onChange={(event) => setLeadForm((current) => ({ ...current, email: event.target.value }))} /></label>
          <label data-testid="quick-lead-phone-label">Telefono<input data-testid="quick-lead-phone-input" value={leadForm.phone} onChange={(event) => setLeadForm((current) => ({ ...current, phone: event.target.value }))} /></label>
        </div>
        <label data-testid="quick-lead-service-label">Tipo di evento (facoltativo)<input data-testid="quick-lead-service-input" value={leadForm.service_type} onChange={(event) => setLeadForm((current) => ({ ...current, service_type: event.target.value }))} /></label>
        <label data-testid="quick-lead-message-label">Note<textarea data-testid="quick-lead-message-input" value={leadForm.message} onChange={(event) => setLeadForm((current) => ({ ...current, message: event.target.value }))} placeholder="Es. contattato telefonicamente, richiesta per un matrimonio a giugno…" /></label>
        <div className="modal-actions">
          <button type="button" className="secondary-button" data-testid="quick-lead-cancel" onClick={() => setLeadForm(null)}>Annulla</button>
          <button type="submit" className="primary-button" data-testid="quick-lead-submit" disabled={busy}>Crea lead</button>
        </div>
      </form>
    </div>}
  </div>;
};
